import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import { DateType } from 'ui/datePicker';

/**
 * Options used to configure the `useSearchParamState` hook.
 */
export interface UseSearchParamStateHookOptions<StateType> {
  /**
   * The key of the URL search parameter on which state will be stored.
   *
   * Warning: If multiple instances of the `useSearchParamState` hook
   * are mounted concurrently with the same `searchParamKey`, they will
   * share the same state value and can interfere with each other.
   */
  searchParamKey: string;

  /**
   * When the `useSearchParamState` hook's state is set to this value,
   * the query parameter keyed by `searchParamKey` is removed from the
   * URL. Also, when the query parameter keyed  by `searchParamKey` is
   * not set in the URL, this value is returned as the hook's state
   * instead.
   */
  stateValueWhenSearchParamIsUnset: StateType;

  /**
   * A function that deterministically converts an arbitrary state type into a
   * string. When state is set with the `useSearchParamState` hook, it is
   * serialized into a string using this function before being stitched into
   * the current URL.
   *
   *  For common state data types, serializers can be found in:
   *
   * - `serializationOptionsForStringValue`
   * - `serializationOptionsForStringArrayValue`
   * - `serializationOptionsForNumberValue`
   */
  serialize: (nextValue: StateType) => string;

  /**
   * A function that deterministically converts a string-serialized state value
   * into a deserialized object/primative. When the browser navigates to a URL
   * with a new value for the search parameter keyed by `searchParamKey`, this
   * function is used to deserialize the string value of the URL parameter to
   * the data-type of the state managed by this hook.
   *
   *  For common state data types, deserializers can be found in:
   *
   * - `serializationOptionsForStringValue`
   * - `serializationOptionsForStringArrayValue`
   * - `serializationOptionsForNumberValue`
   */
  deserialize: (currentSerializedValue: string) => StateType;

  /**
   * Whether or not the `useSearchParamState` hook should push changes to its
   * state to the browser's history. If `true`, an entry will be pushed to the
   * browser's history stack with the new URL after a state change, allowing the
   * user to hit the "back" button to page through different versions of the state.
   */
  pushChangesToBrowserHistory?: boolean;
}

/**
 * State hook that stores component state in the current URL's search parameters.
 * This hook allows multiple components to share global state via the current URL,
 * allows components to persist state across reloads, and allows users to bookmark/
 * send a permalinked URL to a particular component state.
 *
 * The state setter function that is returned by this hook is guaranteed to maintain
 * a consistent identity across render cycles.
 *
 * When state is managed by this hook, it is serialized to/from a URL-safe string.
 * Therefore, to support different data types for state, this hook accepts custom
 * serialize/deserialize functions to convert state to/from a URL-safe string. For
 * common state data types, serializer/deserializer pairs can be found in:
 *
 * - `serializationOptionsForStringValue`
 * - `serializationOptionsForStringArrayValue`
 * - `serializationOptionsForNumberValue`
 *
 * Example hook usage:
 *
 * ```javascript
 * const [filtersArray, setFiltersArray] = useSearchParamState({
 *   searchParamKey: 'filters',
 *   stateValueWhenSearchParamIsUnset: [],
 *   ...serializationOptionsForStringArrayValue,
 * });
 * ```
 *
 * Warning: Unlike React's `useState` hook, which allows a component to maintain
 * state that is isolated to each instance of the `useState` hook, the
 * `useSearchParamState` hook stores state in the URL (global rather than isolated),
 * which means that multiple components keying their state on the same URL search
 * parameter concurrently can interfere with each other's state.
 */
export const useSearchParamState = <StateType>({
  searchParamKey,
  stateValueWhenSearchParamIsUnset,
  serialize,
  deserialize,
  pushChangesToBrowserHistory = false,
}: UseSearchParamStateHookOptions<StateType>): [StateType, (nextValue: StateType) => void] => {
  // Connect to react-router and subscribe to location changes
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  /**
   * Memoized, serialized copy of `stateValueWhenSearchParamIsUnset`. Computed once on mount; does
   * not get recomputed if `stateValueWhenSearchParamIsUnset` or `serialize` change.
   */
  const serializedDefaultValue = useMemo(
    () => serialize(stateValueWhenSearchParamIsUnset),
    [stateValueWhenSearchParamIsUnset],
  );

  /**
   * Memoized, deserialized value of the query parameter keyed by `searchParamKey`. Only changes
   * if a page navigation results in a change in the value of the query parameter keyed by `searchParamKey`.
   */
  const value = useMemo(
    () => deserialize(searchParams.get(searchParamKey) || serializedDefaultValue),
    [searchParams.get(searchParamKey) || serializedDefaultValue],
  );

  /**
   * Memoized callback function for setting the state value of the hook.
   *
   * If this callback is invoked with a value that, when serialized, causes a change to the current
   * URL's search parameter string, the URL is updated and the component is rerendered. All other
   * invocations do not cause a page navigation to prevent duplicate entries being added to browser history.
   *
   * If this callback is invoked with the initial value of the `stateValueWhenSearchParamIsUnset` option
   * as `nextValue`, the query parameter keyed by `searchParamKey` is removed from the URL.
   *
   * Note: Declared once on mount; reference/function identity is guaranteed to remain
   * constant in between render cycles (like useState's `setValue`). `setValue` uses the `serialize` and
   * `pushChangesToBrowserHistory` option references from the first render cycle, and does not
   * receive changes to these options in subsequent render cycles.
   */
  const setValue = useCallback(
    (
      /** The new state value for this hook */
      nextValue: StateType,
    ) => {
      const currentSearchParamsString = window.location.search.slice(1);
      const nextSerializedValue = serialize(nextValue);
      const nextSearchParams = new URLSearchParams(currentSearchParamsString);

      // Apply change to new query params object
      if (nextSerializedValue !== serializedDefaultValue) {
        nextSearchParams.set(searchParamKey, nextSerializedValue);
      } else {
        nextSearchParams.delete(searchParamKey);
      }

      // If this invocation would change the URL, trigger a page navigation via `history`
      const nextSearchParamsString = nextSearchParams.toString();
      if (nextSearchParamsString !== currentSearchParamsString) {
        const nextPath = `${window.location.pathname}?${nextSearchParamsString}`;
        if (pushChangesToBrowserHistory) {
          history.push(nextPath);
        } else {
          history.replace(nextPath);
        }
      }
    },
    [serializedDefaultValue],
  );

  return [value, setValue];
};

/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage string-type state. Spread this object into the hook options object to
 * use string-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForStringValue = {
  serialize: (nextValue: string) => nextValue,
  deserialize: (currentSerializedValue: string) => currentSerializedValue,
};

/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage string-array-type state. Spread this object into the hook options object to
 * use string-array-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForStringArrayValue = {
  serialize: (nextValue: string[]) => nextValue.join(','),
  deserialize: (currentSerializedValue: string) =>
    currentSerializedValue.length ? currentSerializedValue.split(',') : [],
};

/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage numeric-type state. Spread this object into the hook options object to
 * use numeric-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForNumberValue = {
  serialize: (nextValue: number) => nextValue.toString(),
  deserialize: (currentSerializedValue: string) => parseInt(currentSerializedValue, 10),
};

/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage boolean-type state. Spread this object into the hook options object to
 * use boolean-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForBooleanValue = {
  serialize: (nextValue: boolean) => nextValue.toString(),
  deserialize: (currentSerializedValue: string) =>
    Boolean(currentSerializedValue.toLowerCase() === 'true'),
};

/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage numeric-array-type state. Spread this object into the hook options object to
 * use numeric-array-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForNumberArrayValue = {
  serialize: (nextValue: number[]) => nextValue.join(','),
  deserialize: (currentSerializedValue: string) =>
    currentSerializedValue.length
      ? currentSerializedValue
          .split(',')
          .map((value) => parseInt(value, 10))
          .filter((value) => !Number.isNaN(value))
      : [],
};

/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage boolean-array-type state. Spread this object into the hook options object to
 * use boolean-array-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForBooleanArrayValue = {
  serialize: (nextValue: boolean[]) => nextValue.join(','),
  deserialize: (currentSerializedValue: string) =>
    currentSerializedValue.length
      ? currentSerializedValue.split(',').map((value) => value.toLowerCase() === 'true')
      : [],
};

/**
 * Factory function that creates serializer/deserializer options for instances of the
 * `useSearchParamState` that manage enum-type state. Spread the returned object of this
 * function into the hook options object to use enum-state serialization with `useSearchParamState`.
 */
export const createSerializationOptionsForEnumValue = <EnumValueType>(options: {
  /** Returned by hook when search param value is not a value in the enum */
  stateValueWhenSearchParamIsInvalid: EnumValueType;
  enumKeyValueMap: Record<string, EnumValueType>;
}) => ({
  serialize: (nextValue: EnumValueType) => `${nextValue}`,
  deserialize: (currentSerializedValue: string) =>
    Object.values(options.enumKeyValueMap).find((value) => `${value}` === currentSerializedValue) ||
    options.stateValueWhenSearchParamIsInvalid,
});

/**
 * Factory function that creates serializer/deserializer options for instances of the
 * `useSearchParamState` that manage enum-array-type state. Spread the returned object of this
 * function into the hook options object to use enum-array state serialization with `useSearchParamState`.
 */

export const createSerializationOptionsForEnumArrayValue = <EnumValueType>(options: {
  enumKeyValueMap: Record<string, EnumValueType>;
}) => ({
  serialize: (nextValue: string[]) => nextValue.map((value) => `${value}`).join(','),
  deserialize: (currentSerializedValue: string) => {
    const subStrings = currentSerializedValue.split(',');
    return currentSerializedValue.length
      ? Object.values(options.enumKeyValueMap).filter((value) => subStrings.includes(`${value}`))
      : [];
  },
});

/**
 * Serializer/deserializer options for when you need to provide a custom function for deserialization,
 * if that function does not return anything, fall back to the default value.  The value T defines what
 * the function will return;
 */
export const createCustomDeserializationOptions = <T>(options: {
  /** Returned by hook when the value is not return from the custom deserialize function */
  stateValueWhenSearchParamIsInvalid: T;
  deserializeFn: (currentSerializedValue: string | undefined) => T;
}) => ({
  serialize: (nextValue: T) => `${nextValue}`,
  deserialize: (currentSerializedValue: string) =>
    options.deserializeFn(currentSerializedValue) || options.stateValueWhenSearchParamIsInvalid,
});
/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage moment-type state. Spread this object into the hook options object to
 * use moment-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForDateTypeValue = {
  serialize: (nextValue: DateType) => nextValue?.toISOString() || '',
  deserialize: (currentSerializedValue: string): DateType =>
    currentSerializedValue ? moment(currentSerializedValue) : null,
};

/**
 * Serializer/deserializer options for instances of the `useSearchParamState` that
 * manage moment-type state. Spread this object into the hook options object to
 * use moment-state serialization with `useSearchParamState`.
 */
export const serializationOptionsForDateTypeToTimestampValue = {
  serialize: (value: DateType) => value?.valueOf()?.toString() || '',
  deserialize: (value: string): DateType => {
    if (!value) return null;

    const _value = value?.toString();
    const isNumeric = /^\d+$/.test(_value); // date must be a unix timestamp

    if (isNumeric) {
      const timestamp = Number(_value);
      return moment.utc(timestamp);
    }

    return null;
  },
};
